// src/app/App.jsx
import './App.css';
import '../components/footerAndHeader/footer/footerImagePreload';

import { useEffect } from 'react';
import { Route, Routes, BrowserRouter, useNavigate, useLocation, Navigate } from 'react-router-dom';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import CssBaseline from '@mui/material/CssBaseline';
import { ThemeProvider } from '@mui/material/styles';
import { theme } from '../styles/theme';

// i18n / dayjs
import i18n from '../i18n';
import dayjs from 'dayjs';
// строки интерфейса MUI пикеров
import { ruRU, enUS, deDE } from '@mui/x-date-pickers/locales';

import { doc, getDoc, setDoc, updateDoc } from 'firebase/firestore';
import { db } from './firebase';
import { useDispatch, useSelector } from 'react-redux';
import { setUser, setUserInitialized, selectUsers } from '../features/users/usersSlice';
import { fetchPets } from '../features/pets/petsSlice';
import { fetchEventTypes, hydrateEventTypesFromCache } from '../features/eventTypes/eventTypesSlice';

import LoginPage from '../components/loginPage/loginPage';
import { AdminLoginPage } from '../components/loginPage/adminLoginPage';
import { WebSunsetPage } from '../components/loginPage/webSunsetPage';
import Loader from '../components/loader/loader';
import UnauthenticatedHome from '../components/landingPage/unauthenticatedHome';
import SupportPage from '../components/landingPage/supportPage';
import DeleteAccountPage from '../components/landingPage/deleteAccountPage';
import FooterAndHeader from '../components/footerAndHeader/footerAndHeader';

import { onAuthStateChanged } from 'firebase/auth';
import { auth } from './firebase';
import { GlobalStyle } from '../styles/globalStyles';
import ProtectedAdminRoute from './protectedAdminRoute';
import { useIsAdmin } from '../hooks/useIsAdmin';
import AdminUsersContainer from '../components/admin/users/adminUsersContainer';
import AdminFeedbackContainer from '../components/admin/feedback/adminFeedbackContainer';
import AdminUpdatesContainer from '../components/admin/updates/adminUpdatesContainer';
import AdminArticlesContainer from '../components/admin/articles/adminArticlesContainer';
import AdminArticleCategories from '../components/admin/articleLookups/adminArticleCategories';
import AdminArticlePetTags from '../components/admin/articleLookups/adminArticlePetTags';
import AdminEventTypesContainer from '../components/admin/eventTypes/adminEventTypesContainer';
import AdminLegalPagesContainer from '../components/admin/legalPages/adminLegalPagesContainer';
import AdminFaqItems from '../components/admin/faq/adminFaqItems';
import AdminDevServicesContainer from '../components/admin/devServices/adminDevServicesContainer';
import ErrorBoundary from '../components/shared/errorBoundary/errorBoundary';
import LegalPageScreen from '../components/legal/legalPageScreen';
import { APP_NAME } from '../config/app';
import { ADMIN_WEB_LOGIN_PATH } from '../config/adminWebAccess';

function SignedInHome() {
  const { isAdmin, loading } = useIsAdmin();
  if (loading) return <Loader />;
  if (isAdmin) return <Navigate to="/admin/users" replace />;
  return <WebSunsetPage signedIn />;
}

function AppRoutes() {
  const { currentUser, userInitialized } = useSelector(selectUsers);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (!userInitialized || !currentUser) return;
    localStorage.removeItem('justRegistered');

    if (
      location.pathname.startsWith('/login') ||
      location.pathname.startsWith('/reset-password')
    ) {
      navigate('/', { replace: true });
      return;
    }

    if (location.pathname === ADMIN_WEB_LOGIN_PATH) {
      navigate('/admin/users', { replace: true });
    }
  }, [userInitialized, currentUser, navigate, location.pathname]);

  if (!userInitialized) return <Loader />;

  return (
    <Routes>
      <Route path="/privacy-policy" element={<LegalPageScreen pageId="privacy-policy" />} />
      <Route path="/terms-of-service" element={<LegalPageScreen pageId="terms-of-service" />} />
      <Route path="/support" element={<SupportPage />} />
      <Route path="/delete-account" element={<DeleteAccountPage />} />

      {currentUser ? (
        <>
          <Route path="/admin" element={<FooterAndHeader />}>
            <Route index element={<Navigate to="/admin/users" replace />} />
            <Route
              path="users"
              element={<ProtectedAdminRoute><AdminUsersContainer /></ProtectedAdminRoute>}
            />
            <Route
              path="feedback"
              element={<ProtectedAdminRoute><AdminFeedbackContainer /></ProtectedAdminRoute>}
            />
            <Route
              path="updates"
              element={<ProtectedAdminRoute><AdminUpdatesContainer /></ProtectedAdminRoute>}
            />
            <Route
              path="articles"
              element={<ProtectedAdminRoute><AdminArticlesContainer /></ProtectedAdminRoute>}
            />
            <Route
              path="article-categories"
              element={<ProtectedAdminRoute><AdminArticleCategories /></ProtectedAdminRoute>}
            />
            <Route
              path="article-pet-tags"
              element={<ProtectedAdminRoute><AdminArticlePetTags /></ProtectedAdminRoute>}
            />
            <Route
              path="event-types"
              element={<ProtectedAdminRoute><AdminEventTypesContainer /></ProtectedAdminRoute>}
            />
            <Route
              path="legal-pages"
              element={<ProtectedAdminRoute><AdminLegalPagesContainer /></ProtectedAdminRoute>}
            />
            <Route
              path="faq"
              element={<ProtectedAdminRoute><AdminFaqItems /></ProtectedAdminRoute>}
            />
            <Route
              path="dev-services"
              element={<ProtectedAdminRoute><AdminDevServicesContainer /></ProtectedAdminRoute>}
            />
          </Route>
          <Route path={ADMIN_WEB_LOGIN_PATH} element={<Navigate to="/admin/users" replace />} />
          <Route path="/" element={<SignedInHome />} />
          <Route path="*" element={<WebSunsetPage signedIn />} />
        </>
      ) : (
        <>
          <Route path="/" element={<UnauthenticatedHome />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/reset-password" element={<Navigate to="/login" replace />} />
          <Route path={ADMIN_WEB_LOGIN_PATH} element={<AdminLoginPage />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </>
      )}
    </Routes>
  );
}


function App() {
  const dispatch = useDispatch();

  useEffect(() => {
    document.title = APP_NAME;
  }, []);

  // синхронизация i18n → dayjs (+ сохранение выбранного языка в профиль при наличии auth)
  useEffect(() => {
    dispatch(hydrateEventTypesFromCache());

    const applyLocale = async (lngRaw, persist) => {
      const lng = (lngRaw || i18n.resolvedLanguage || i18n.language || 'ru').split('-')[0];

      // <html lang="...">
      document.documentElement.lang = lng;

      // dayjs локаль (месяцы/дни для адаптера и форматирования)
      dayjs.locale(lng);

      // сохраняем в профиль только смену языка (не язык браузера при загрузке — он затёр бы выбор из приложения)
      if (persist && auth.currentUser && db) {
        try {
          await updateDoc(doc(db, 'users', auth.currentUser.uid), { locale: lng });
        } catch {}
      }
    };

    const handler = (lng) => applyLocale(lng, true);
    i18n.on('languageChanged', handler);
    // выставим корректную локаль один раз при монтировании
    applyLocale(i18n.resolvedLanguage, false);

    return () => i18n.off('languageChanged', handler);
  }, [dispatch]);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) {
        dispatch(setUser({ id: user.uid, email: user.email, telegramChatId: null }));
        dispatch(fetchPets(user.uid));

        void (async () => {
          try {
            if (db) {
              const ref = doc(db, 'users', user.uid);
              const snap = await getDoc(ref);
              const detected = (i18n.resolvedLanguage || i18n.language || 'ru').split('-')[0];

              if (snap.exists()) {
                const profile = snap.data();
                const profileLocale = profile.locale;
                if (profileLocale && profileLocale !== detected) {
                  await i18n.changeLanguage(profileLocale);
                } else if (!profileLocale) {
                  await updateDoc(ref, { locale: detected });
                }

                const plan = profile.plan || 'Free';
                const telegramChatId = profile.telegramChatId || null;
                dispatch(setUser({
                  id: user.uid,
                  email: user.email,
                  plan,
                  telegramChatId,
                }));
              } else {
                await setDoc(ref, {
                  email: user.email,
                  name: user.displayName || '',
                  telegramChatId: null,
                  plan: 'Free',
                  locale: detected,
                }, { merge: true });
                dispatch(setUser({
                  id: user.uid,
                  email: user.email,
                  plan: 'Free',
                  telegramChatId: null,
                }));
              }
            }
          } catch {}
        })();
      } else {
        dispatch(setUser(null));
      }

      dispatch(fetchEventTypes());
      dispatch(setUserInitialized(true));
    });

    return () => unsubscribe();
  }, [dispatch]);

  // 👇 выберем короткий язык и тексты интерфейса пикера
  const lngShort = (i18n.resolvedLanguage || i18n.language || 'ru').split('-')[0];
  const pickerLocaleTextByLng = {
    ru: ruRU.components.MuiLocalizationProvider.defaultProps.localeText,
    en: enUS.components.MuiLocalizationProvider.defaultProps.localeText,
    de: deDE.components.MuiLocalizationProvider.defaultProps.localeText,
    sr: enUS.components.MuiLocalizationProvider.defaultProps.localeText,
  };
  const localeText = pickerLocaleTextByLng[lngShort] || enUS.components.MuiLocalizationProvider.defaultProps.localeText;

  return (
    <LocalizationProvider
      dateAdapter={AdapterDayjs}
      adapterLocale={lngShort}      // 👈 месяцы/дни берутся из dayjs локали
      localeText={localeText}       // 👈 кнопки/лейблы UI пикера от MUI
    >
      <CssBaseline />
      <BrowserRouter>
        <ThemeProvider theme={theme}>
          <GlobalStyle />
          <ErrorBoundary>
            <AppRoutes />
          </ErrorBoundary>
        </ThemeProvider>
      </BrowserRouter>
    </LocalizationProvider>
  );
}

export default App;


// import './App.css';
// import '../components/footerAndHeader/footer/footerImagePreload';

// // React и библиотеки
// import { useEffect, useState } from 'react';
// import { Route, Routes, BrowserRouter, useNavigate, useLocation, Navigate } from 'react-router-dom';
// import { LocalizationProvider } from '@mui/x-date-pickers';
// import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
// import CssBaseline from '@mui/material/CssBaseline';
// import { ThemeProvider } from '@mui/material/styles';
// import { theme } from '../styles/theme';

// //firebase
// import i18n from '../i18n';
// import { doc, getDoc, setDoc, updateDoc } from 'firebase/firestore';
// import { db } from './firebase'; // если db уже экспортируется из твоего firebase.js


// // Redux
// import { useDispatch, useSelector } from 'react-redux';
// import { setUser, setUserInitialized, selectUsers } from '../features/users/usersSlice';
// import { fetchPets } from '../features/pets/petsSlice';

// // Компоненты
// import MainScreen from '../components/main/mainScreen';


// import LoginPage from '../components/loginPage/loginPage';
// import Onboarding from '../components/onboarding/onboarding';
// import Loader from '../components/loader/loader';
// import WelcomePage from '../components/welcomePage/welcomePage';
// import Explore from '../components/explore/explore';
// import FooterAndHeader from '../components/footerAndHeader/footerAndHeader';
// import StepStart from '../components/onboarding/steps/stepStart';
// import UserAccount from '../components/petsProfile/petsProfile/petsProfile';

// import Settings from '../components/account/account';
// import AddEventForm from '../components/addEditEvent/addEventForm/addEventForm'

// // Firebase
// import { onAuthStateChanged } from 'firebase/auth';
// import { auth } from './firebase';

// import { GlobalStyle } from '../styles/globalStyles';
// import EditEventForm from '../components/addEditEvent/editEvent/editEventForm';
// import Notifications from '../components/notifications/notifiications';
// import EventsContainer from '../components/events/events/eventsContainer';
// import AddEventContainer from '../components/addEditEvent/addEvent/addEventContainer'

// import AddEditPetContainer from '../components/petsProfile/addEditPet/addEditPetContainer';
// import Account from '../components/account/account';
// import { useMediaQuery } from 'react-responsive';

// import ProtectedAdminRoute from './protectedAdminRoute';

// import AdminUsersContainer from '../components/admin/users/adminUsersContainer';
// import AdminFeedbackContainer from '../components/admin/feedback/adminFeedbackContainer';

// import dayjs from "dayjs";

// function AppRoutes() {
//   const { currentUser, userInitialized } = useSelector(selectUsers);
//   const navigate = useNavigate();
//   const location = useLocation();
//   const [redirectPending, setRedirectPending] = useState(true);

//   const [justRegisteredFlag, setJustRegisteredFlag] = useState(() =>
//     localStorage.getItem('justRegistered')
//   );

//   const isDesktop = useMediaQuery({ minWidth: '800px' });

  

//   useEffect(() => {
//     const interval = setInterval(() => {
//       const flag = localStorage.getItem('justRegistered');
//       if (flag !== justRegisteredFlag) {
//         setJustRegisteredFlag(flag);
//       }
//     }, 100);

//     return () => clearInterval(interval);
//   }, [justRegisteredFlag]);

//   useEffect(() => {
//     if (!userInitialized || !currentUser) {
//       setRedirectPending(false);
//       return;
//     }

//     if (justRegisteredFlag) {
//       localStorage.removeItem('justRegistered');
//       navigate('/start', { replace: true });
//       return;
//     }

//     // редирект авторизованного пользователя со /login на /
//     if (location.pathname.startsWith('/login')) {
//       navigate('/', { replace: true });
//       return;
//     }

//     setRedirectPending(false);
//   }, [userInitialized, currentUser, justRegisteredFlag, navigate, location.pathname]);

//   const shouldBlockUntilRedirect =
//     currentUser && justRegisteredFlag && location.pathname === '/' && redirectPending;

//   if (!userInitialized || shouldBlockUntilRedirect) return <Loader />;

//   return (
    
//     <Routes>
//       {currentUser ? (
//         <>
//           <Route path="/start" element={<StepStart/>} />
//           <Route path="/onboarding" element={<Onboarding />} />
//           <Route path="/edit-pet" element={<AddEditPetContainer />} />
//           {!isDesktop && <Route path="account" element={<Account />} />}

//           <Route path="add-event" element={<AddEventForm />} />
//           <Route path="/edit-event" element={<EditEventForm />} />
//           <Route path="/notifications" element={<Notifications />} />

//           <Route path="/" element={<FooterAndHeader />}>
//             {/* Админский экран */}
//             <Route
//               path="/admin/users"
//               element={<ProtectedAdminRoute><AdminUsersContainer /></ProtectedAdminRoute>}
//             />
//             <Route
//               path="/admin/feedback"
//               element={<ProtectedAdminRoute><AdminFeedbackContainer /></ProtectedAdminRoute>}
//   />
//             {/* на всякий случай поддержим старый /admin */}
//             <Route path="/admin" element={<Navigate to="/admin/users" replace />} />

//             {/* ГЛАВНАЯ: теперь через шлюз */}
//             <Route index element={<MainScreen  />} />

//             <Route path="profile" element={<UserAccount />} />
//             <Route path="add" element={<AddEventContainer />} />
//             <Route path="explore" element={<Explore />} />
//             <Route path="events" element={<EventsContainer />} />
//             {isDesktop && <Route path="account" element={<Account />} />}
//           </Route>
//         </>
//       ) : (
//         <>
//           <Route path="/" element={<WelcomePage />} />
//           <Route path="/login" element={<LoginPage />} />
//           <Route path="*" element={<Navigate to="/" replace />} />
//         </>
//       )}
//     </Routes>
//   );
// }

// function App() {
//   const dispatch = useDispatch();

//   // при ручном переключении RU/SR/EN пишем в профиль
//   useEffect(() => {
//     const handler = async (lng) => {
//       // html lang для a11y/SEO
//       document.documentElement.lang = lng;
//       // если пользователь авторизован и есть db — сохраним выбор
//       if (auth.currentUser && db) {
//         try {
//           await updateDoc(doc(db, 'users', auth.currentUser.uid), { locale: lng });
//         } catch (e) {
//           // молча игнорируем: офлайн/нет прав — UX не страдает
//         }
//       }
//     };
//     i18n.on('languageChanged', handler);
    
//     return () => i18n.off('languageChanged', handler);
//   }, []);

//   useEffect(() => {
//     const unsubscribe = onAuthStateChanged(auth, async (user) => {
//       if (user) {
//         dispatch(setUser({ id: user.uid, email: user.email }));
//         dispatch(fetchPets(user.uid));

//         // ---- язык профиля ----
//         try {
//           if (db) {
//             const ref = doc(db, 'users', user.uid);
//             const snap = await getDoc(ref);
//             const detected = (i18n.resolvedLanguage || i18n.language || 'ru').split('-')[0];
//             if (snap.exists()) {
//               const profileLocale = snap.data().locale;
//               if (profileLocale && profileLocale !== detected) {
//                 await i18n.changeLanguage(profileLocale);
//               } else if (!profileLocale) {
//                 await updateDoc(ref, { locale: detected });
//               }
//             } else {
//               await setDoc(ref, { locale: detected }, { merge: true });
//             }
//           }
//         } catch (e) {
//           // ок, просто оставим язык как есть
//         }
//         // ---- /язык профиля ----

//       } else {
//         dispatch(setUser(null));
//       }
//       dispatch(setUserInitialized(true));
//     });

//     return () => unsubscribe();
//   }, [dispatch]);

//   return (
//     <LocalizationProvider dateAdapter={AdapterDayjs} >
//       <CssBaseline />
//       <BrowserRouter>
//         <ThemeProvider theme={theme}>
//           <GlobalStyle />
//           <AppRoutes />
//         </ThemeProvider>
//       </BrowserRouter>
//     </LocalizationProvider>
//   );
// }

// export default App;
